import React from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { Button, CardHeader, Alert } from "@mui/material";
import AuditHistoryHeader from "../components/history/AuditHistoryHeader";
import { AuditService } from "../services/auditService";
import { UserContext } from "../contexts/userContext";
import AuditorFilter from "../components/history/AuditorFilter";
import RxFilter from "../components/history/HistoryRxFilter";
import AuditDateRangePicker from "../components/history/AuditDateRangePicker";
import { historyGen } from "../utils/dictGen";
import { useRecoilState } from "recoil";
import { historyFilterAtom } from "../atoms/atoms";
import HistoryTypeFilter from "../components/history/HistoryTypeFilter";
import HistoryOutcomeFilter from "../components/history/HistoryOutcomeFilter";
import ErrorPage from "./ErrorPage";
import dayjs from "dayjs";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  ExportWarningDialog,
  ExportProgressDialog,
  RecordCountDisplay,
} from "../components/history/ExportDialogs";

// Configuration
const EXPORT_THRESHOLDS = {
  WARNING: 1000, // Show warning dialog
  CRITICAL: 2000, // Show strong warning
  MAX: 5000, // Absolute maximum
};

export default function History() {
  const [historyFilters, setHistoryFilters] = useRecoilState(historyFilterAtom);
  const [encounters, setEncounters] = React.useState([]);
  const [referrals, setReferrals] = React.useState([]);
  const [compliance, setCompliance] = React.useState([]);
  const [dataset, setDataset] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [load1, setLoad1] = React.useState(false);
  const [load2, setLoad2] = React.useState(false);
  const [load3, setLoad3] = React.useState(false);
  const [load4, setLoad4] = React.useState(false);

  // Export-specific state
  const [exportWarningOpen, setExportWarningOpen] = React.useState(false);
  const [exportProgress, setExportProgress] = React.useState(0);
  const [isExporting, setIsExporting] = React.useState(false);
  const [exportError, setExportError] = React.useState(null);

  const {
    getAllAuditedAudits,
    getAllAuditedEncounters,
    getAllAuditedReferrals,
    getAllAuditedCompliance,
    getNotesForExport,
  } = AuditService();

  const userCtx = React.useContext(UserContext);
  const user = userCtx.user;
  const userPermissions = user.user_secondary_roles.filter((r) => r == 7);

  const getDefaultDates = () => {
    return {
      start: dayjs().subtract(2, "week").startOf("day").toISOString(),
      end: dayjs().endOf("day").toISOString(),
    };
  };

  const resetData = () => {
    const { start, end } = getDefaultDates();
    setDataset([]);
    setEncounters([]);
    setCompliance([]);
    setReferrals([]);
    // setHistoryFilters((prev) => ({
    //   ...prev,
    //   auditor: "",
    //   rx_id: "",
    //   auditType: "",
    //   outcome: "",
    //   start_date: start,
    //   end_date: end,
    //   filtered: false,
    // }));
  };

  const getData = () => {
    setLoading(true);
    resetData();
    getAllAuditedEncounters(
      !user.hierarchy ? [user.user_id] : [...user.hierarchy, user.user_id],
      historyFilters.start_date,
      historyFilters.end_date,
      historyFilters.auditor,
      historyFilters.rx_id,
      historyFilters.auditType,
      historyFilters.outcome,
      (data) => {
        const allInfo = historyGen(data, "Encounter");
        setEncounters(allInfo);
        setLoad1(true);
      },
      (err) => {
        console.log(err);
        setLoad1(true);
      }
    );

    getAllAuditedReferrals(
      !user.hierarchy ? [user.user_id] : [...user.hierarchy, user.user_id],
      historyFilters.start_date,
      historyFilters.end_date,
      historyFilters.auditor,
      historyFilters.rx_id,
      historyFilters.auditType,
      historyFilters.outcome,
      (data) => {
        const allInfo = historyGen(data, "Referral");
        setReferrals(allInfo);
        setLoad2(true);
      },
      (err) => {
        console.log(err);
        setLoad2(true);
      }
    );

    getAllAuditedCompliance(
      !user.hierarchy ? [user.user_id] : [...user.hierarchy, user.user_id],
      historyFilters.start_date,
      historyFilters.end_date,
      historyFilters.auditor,
      historyFilters.rx_id,
      historyFilters.auditType,
      historyFilters.outcome,
      (data) => {
        const allInfo = historyGen(data, "Compliance");
        setCompliance(allInfo);
        setLoad4(true);
      },
      (err) => {
        console.log(err);
        setLoad4(true);
      }
    );
  };

  React.useEffect(() => {
    getData();
  }, []);

  React.useEffect(() => {
    setLoad1(false);
    setLoad2(false);
    setLoad4(false);
  }, [historyFilters]);

  React.useEffect(() => {
    if (load1 && load2 && load4) setLoading(false);
  }, [load1, load2, load4]);

  React.useEffect(() => {
    console.log('Before', encounters, compliance)
    setDataset([...encounters, ...referrals, ...compliance]);
    console.log('After', encounters, compliance)
  }, [encounters, referrals, compliance]);

  React.useEffect(() => {
    if (
      !historyFilters.auditor &&
      !historyFilters.rx_id &&
      !historyFilters.auditType &&
      !historyFilters.outcome &&
      !(historyFilters.start_date && historyFilters.end_date)
    ) {
      setHistoryFilters((prev) => ({
        ...prev,
        filtered: false,
      }));
    }
  }, [
    historyFilters.auditor,
    historyFilters.rx_id,
    historyFilters.auditType,
    historyFilters.outcome,
    historyFilters.start_date,
    historyFilters.end_date,
  ]);

  const handleReset = async () => {
    resetData();
    setHistoryFilters((prev) => ({
      ...prev,
      auditor: "",
      rx_id: "",
      auditType: "",
      outcome: "",
      start_date: null,
      end_date: null,
      filtered: false,
    }));
    getData();
  };

  const handleSubmit = () => {
    getData();
  };

  // Check export size and show warning if needed
  const handleExport = () => {
    const recordCount = dataset.length;
    setExportError(null);

    if (recordCount === 0) {
      alert("No data to export");
      return;
    }

    // Check thresholds
    if (recordCount > EXPORT_THRESHOLDS.MAX) {
      alert(
        `Cannot export more than ${EXPORT_THRESHOLDS.MAX.toLocaleString()} records. Please refine your filters to reduce the dataset.`
      );
      return;
    }

    if (recordCount > EXPORT_THRESHOLDS.WARNING) {
      setExportWarningOpen(true);
    } else {
      // Proceed directly for small exports
      performExport();
    }
  };

  // Perform the actual export
  const performExport = async () => {
    setExportWarningOpen(false);
    setIsExporting(true);
    setExportProgress(0);
    setExportError(null);

    try {
      await performClientSideExport();
    } catch (error) {
      console.error("Export error:", error);
      setExportError(error.message || "An error occurred during export");
      alert("Error exporting data. Please try again with fewer records.");
    } finally {
      setIsExporting(false);
      setExportProgress(0);
    }
  };

  // Client-side export with progress tracking
  const performClientSideExport = async () => {
    const totalRecords = dataset.length;

    try {
      // Step 1: Fetch notes in batches (20% of progress)
      setExportProgress(10);

      // Extract unique rx_prx_ids
      const rxIds = [...new Set(dataset.map((d) => d.rx_prx_id))];
      console.log(`Fetching notes for ${rxIds.length} unique prescriptions`);

      // Fetch notes with proper error handling
      let notesData = {};
      try {
        const response = await new Promise((resolve, reject) => {
          getNotesForExport(
            rxIds,
            (data) => resolve(data),
            (err) => reject(err)
          );
        });

        // Ensure notesData is properly structured
        notesData = response || {};
        console.log(
          `Received notes for ${Object.keys(notesData).length} prescriptions`
        );
      } catch (error) {
        console.error("Error fetching notes:", error);
        // Continue with empty notes if fetch fails
        notesData = {};
      }

      setExportProgress(20);
      console.log(dataset);

      // Step 2: Generate Excel (70% of progress)
      const workbook = await generateExcelWithProgress(
        dataset,
        notesData,
        (progress) => {
          setExportProgress(20 + progress * 0.7);
        }
      );

      // Step 3: Save file (10% of progress)
      setExportProgress(90);
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const fileName = `History_Report_${new Date()
        .toLocaleDateString()
        .replace(/\//g, "-")}.xlsx`;
      saveAs(blob, fileName);
      setExportProgress(100);

      // Don't immediately reset - let the success message show
      await new Promise((resolve) => setTimeout(resolve, 500));
    } catch (error) {
      throw new Error(`Export failed: ${error.message}`);
    }
  };

  // Generate Excel with progress callback
  const generateExcelWithProgress = async (data, notesMap = {}, onProgress) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Audit History");

    // Add header info (these are already on E2, E3, which is fine)
    worksheet.getCell(
      "E2"
    ).value = `User: ${user.last_name}, ${user.first_name}`;
    worksheet.getCell(
      "E3"
    ).value = `Audit Date: ${new Date().toLocaleDateString()}`;

    // Define column configurations with header text, key, and width
    // We will use this array to build both the header row and the column definitions.
    const columnDefinitions = [
      { header: "RX PRX ID", key: "rx_prx_id", width: 15 },
      { header: "Auditor", key: "auditor", width: 20 },
      { header: "Date Dispensed", key: "date_dispensed", width: 15 },
      { header: "Amount", key: "amount", width: 12 },
      { header: "Audit Type", key: "audit_type", width: 15 },
      { header: "Outcome", key: "outcome", width: 20 },
      { header: "Qualified Correctly", key: "qualified_correctly", width: 18 },
      { header: "Status", key: "status", width: 15 },
      { header: "Audit Date Created", key: "audit_date_created", width: 18 },
      { header: "Audit Date Updated", key: "audit_date_updated", width: 18 },
      { header: "Audit Time (Minutes)", key: "audit_time", width: 18 },
      { header: "Pharmacy Name", key: "pharmacy_name", width: 25 },
      { header: "RX Number", key: "rx_number", width: 15 },
      { header: "RX Fill Number", key: "rx_fill_number", width: 15 },
      { header: "Patient Last Name", key: "patient_last_name", width: 20 },
      { header: "Patient First Name", key: "patient_first_name", width: 20 },
      { header: "Patient DOB", key: "patient_dob", width: 12 },
      { header: "Prescriber Name", key: "prescriber_name", width: 25 },
      { header: "RX Date Written", key: "rx_date_written", width: 15 },
      { header: "RX Date Dispensed", key: "rx_date_dispensed", width: 15 },
      { header: "Primary Payor", key: "primary_payor", width: 20 },
      { header: "Secondary Payor", key: "secondary_payor", width: 20 },
      { header: "NDC Number", key: "ndc_number", width: 15 },
      { header: "Drug Name", key: "drug_name", width: 30 },
      { header: "Notes", key: "notes", width: 50 },
    ];

    // Extract headers for row 5
    const headerLabels = columnDefinitions.map((col) => col.header);

    // Set column properties (keys and widths) for data mapping.
    // IMPORTANT: Remove the 'header' property from this array,
    // as we are manually setting the headers on row 5.
    worksheet.columns = columnDefinitions.map((col) => ({
      key: col.key,
      width: col.width,
    }));

    // Manually add the header row at row 5
    const headerRow = worksheet.getRow(5);
    headerRow.values = headerLabels; // Assign the array of header strings
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE0E0E0" },
    };

    // Add data rows - these will automatically start from row 6 after row 5 is populated
    const totalRows = data.length;
    const batchSize = 100;

    for (let i = 0; i < totalRows; i += batchSize) {
      
      const batch = data.slice(i, Math.min(i + batchSize, totalRows));

      batch.forEach((record) => {
        console.log(record);
        // Safely get notes with fallback
        const notes = notesMap[record.rx_prx_id] || record.complianceNote || "";

        // Parse patient name safely
        const patientNameParts = (record.patientName || "")
          .split(",")
          .map((s) => s.trim());
        const patientLastName = patientNameParts[0] || "";
        const patientFirstName = patientNameParts[1] || "";

        worksheet.addRow({
          rx_prx_id: record.rx_prx_id || "",
          auditor: record.auditor || "",
          date_dispensed: record.dateDispensed || "",
          amount: record.potentialSavings
            ? `$${Number(record.potentialSavings).toFixed(2)}`
            : "$0.00",
          audit_type: record.auditType || "",
          outcome: formatOutcome(record.outcome),
          qualified_correctly: formatQualifiedCorrectly(
            record.complianceQual,
            record.auditType
          ),
          status: getStatusName(record.status),
          audit_date_created: formatDate(record.auditDateCreated),
          audit_date_updated: formatDate(record.auditDateUpdated),
          audit_time: record.auditTimeMinutes || "0",
          pharmacy_name: record.pharmacy || "",
          rx_number: record.pk?.rx_number || record.rxNumber || "",
          rx_fill_number: record.fills || "",
          patient_last_name: patientLastName,
          patient_first_name: patientFirstName,
          patient_dob: formatDate(record.dob),
          prescriber_name: record.prescriberName || "",
          rx_date_written: record.dateWritten || "",
          rx_date_dispensed: record.dateDispensed || "",
          primary_payor: record.primaryPayor || "N/A",
          secondary_payor: record.secondaryPayor || "N/A",
          ndc_number: record.ndc || "",
          drug_name: record.drugName || "",
          notes: notes,
        });
      });

      // Update progress
      const progress = Math.min((i + batchSize) / totalRows, 1);
      onProgress(progress);

      // Allow UI to update
      await new Promise((resolve) => setTimeout(resolve, 0));
    }

    onProgress(1);

    // Add autofilter - Ensure it starts from row 5 for headers
    worksheet.autoFilter = {
      from: { row: 5, column: 1 },
      to: { row: worksheet.rowCount, column: columnDefinitions.length },
    };

    // Freeze panes - Freeze up to and including row 5
    worksheet.views = [{ state: "frozen", xSplit: 0, ySplit: 5 }];

    return workbook;
  };

  // Helper functions
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      return date.toLocaleDateString("en-US");
    } catch {
      return "";
    }
  };

  const formatOutcome = (outcome) => {
    if (!outcome) return "";
    // Handle various outcome formats
    if (typeof outcome === "string") {
      // Replace underscores with spaces and capitalize
      return outcome
        .replace(/_/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());
    }
    return outcome.toString();
  };

  const formatQualifiedCorrectly = (value, auditType) => {
    if (auditType !== "Compliance") return "";
    if (value === true || value === "true") return "Yes";
    if (value === false || value === "false") return "No";
    return "";
  };

  const getStatusName = (statusId) => {
    const statusMap = {
      1: "New",
      2: "In Process",
      3: "Completed",
      4: "Submitted",
      5: "Cancelled",
      6: "Pending CE Review",
      7: "Pending",
      8: "Compliant",
      9: "Non-Compliant",
      10: "Aged-Out",
      11: "Ignore",
      12: "Re-Audit",
    };
    return statusMap[statusId] || "Unknown";
  };

  // Warning Dialog Component
  // const ExportWarningDialog = () => {
  //   const recordCount = dataset.length;
  //   const isCritical = recordCount > EXPORT_THRESHOLDS.CRITICAL;
  //   const estimatedTime = Math.ceil(recordCount / 500); // Roughly 500 records per second

  //   return (
  //     <Dialog
  //       open={exportWarningOpen}
  //       onClose={() => setExportWarningOpen(false)}
  //       maxWidth="sm"
  //       fullWidth
  //     >
  //       <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
  //         <WarningIcon color={isCritical ? "error" : "warning"} />
  //         Large Export Warning
  //       </DialogTitle>

  //       <DialogContent>
  //         <DialogContentText>
  //           You are about to export{" "}
  //           <strong>{recordCount.toLocaleString()}</strong> records.
  //         </DialogContentText>

  //         {isCritical && (
  //           <Alert severity="error" sx={{ my: 2 }}>
  //             This is a very large export that may take significant time and
  //             could impact browser performance.
  //           </Alert>
  //         )}

  //         <Box sx={{ my: 2 }}>
  //           <Typography variant="body2" gutterBottom>
  //             <strong>Estimated time:</strong> {estimatedTime} seconds
  //           </Typography>
  //           <Typography variant="body2" gutterBottom>
  //             <strong>Recommended:</strong>{" "}
  //             {isCritical
  //               ? "Consider adding filters to reduce the number of records"
  //               : "This export should complete without issues"}
  //           </Typography>
  //         </Box>

  //         <DialogContentText variant="body2" color="text.secondary">
  //           Tip: For better performance, try filtering by date range or specific
  //           audit types.
  //         </DialogContentText>
  //       </DialogContent>

  //       <DialogActions>
  //         <Button onClick={() => setExportWarningOpen(false)}>Cancel</Button>
  //         <Button
  //           onClick={performExport}
  //           variant="contained"
  //           color={isCritical ? "error" : "primary"}
  //         >
  //           Proceed with Export
  //         </Button>
  //       </DialogActions>
  //     </Dialog>
  //   );
  // };

  // Handle export completion
  const handleExportComplete = () => {
    setTimeout(() => {
      setIsExporting(false);
      setExportProgress(0);
    }, 2000); // Show success message for 2 seconds
  };

  if (userPermissions.length < 1) {
    return <ErrorPage />;
  }

  return (
    <Stack>
      <Box sx={{ width: 1, height: 400 }}>
        <CardHeader
          title="Audit History"
          titleTypographyProps={{
            fontSize: 25,
            variant: "overline",
            textAlign: "center",
            fontStyle: "underline",
          }}
        />
        <Stack
          sx={{ display: "flex", background: "#B9D7EA" }}
          alignItems={"center"}
        >
          <AuditorFilter data={dataset} />
          <RxFilter data={dataset} />
          <HistoryTypeFilter data={dataset} />
          <HistoryOutcomeFilter data={dataset} />
          <AuditDateRangePicker
            original={dataset}
            data={dataset}
            type={"history"}
          />
          <Stack direction={"horizontal"}>
            <Button sx={{ m: 1 }} variant="contained" onClick={handleSubmit}>
              Submit
            </Button>
            <Button sx={{ m: 1 }} variant="contained" onClick={handleReset}>
              Reset All
            </Button>
          </Stack>
        </Stack>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
          }}
        >
          <RecordCountDisplay
            count={dataset.length}
            warningThreshold={EXPORT_THRESHOLDS.WARNING}
            criticalThreshold={EXPORT_THRESHOLDS.CRITICAL}
          />
          <Button
            onClick={handleExport}
            sx={{ margin: 2 }}
            variant="contained"
            disabled={isExporting || dataset.length === 0}
          >
            Download Report
          </Button>
        </Box>

        <AuditHistoryHeader
          encounters={encounters}
          referrals={referrals}
          compliances={compliance}
          loading={loading}
          filter={dataset.map((f) => f.rx_prx_id)}
        />

        <ExportWarningDialog
          open={exportWarningOpen}
          onClose={() => setExportWarningOpen(false)}
          onConfirm={performExport}
          recordCount={dataset.length}
          criticalThreshold={EXPORT_THRESHOLDS.CRITICAL}
        />

        <ExportProgressDialog
          open={isExporting}
          progress={exportProgress}
          recordCount={dataset.length}
          error={exportError}
          onClose={handleExportComplete}
        />
      </Box>
    </Stack>
  );
}
